import "../assets/index-b0a8a48c.css";
import K from "./NcButton.mjs";
import U from "./NcPopover.mjs";
import { G as j } from "../chunks/GenRandomId-cb9ccebe.mjs";
import { t as J } from "../chunks/l10n-1b905a9a.mjs";
import Q, { computed as W } from "vue";
import { D as X } from "../chunks/DotsHorizontal-0ee7d901.mjs";
import { n as Y } from "../chunks/_plugin-vue2_normalizer-71e2aa87.mjs";
const a = ".focusable", Z = {
  name: "NcActions",
  components: {
    NcButton: K,
    DotsHorizontal: X,
    NcPopover: U
  },
  provide() {
    return {
      /**
       * NcActions can be used as:
       * - Application menu (has menu role)
       * - Navigation (has no specific role, should be used an element with navigation role)
       * - Popover with plain text or text inputs (has no specific role)
       * Depending on the usage (used items), the menu and its items should have different roles for a11y.
       * Provide the role for NcAction* components in the NcActions content.
       * @type {import('vue').ComputedRef<boolean>}
       */
      "NcActions:isSemanticMenu": W(() => this.isSemanticMenu)
    };
  },
  props: {
    /**
     * Specify the open state of the popover menu
     */
    open: {
      type: Boolean,
      default: !1
    },
    /**
     * This disables the internal open management,
     * so the actions menu only respects the `open` prop.
     * This is e.g. necessary for the NcAvatar component
     * to only open the actions menu after loading it's entries has finished.
     */
    manualOpen: {
      type: Boolean,
      default: !1
    },
    /**
     * Force the actions to display in a three dot menu
     */
    forceMenu: {
      type: Boolean,
      default: !1
    },
    /**
     * Force the name to show for single actions
     */
    forceName: {
      type: Boolean,
      default: !1
    },
    /**
     * Specify the menu name
     */
    menuName: {
      type: String,
      default: null
    },
    /**
     * Apply primary styling for this menu
     */
    primary: {
      type: Boolean,
      default: !1
    },
    /**
     * Specifies the button type used for trigger and single actions buttons
     * Accepted values: primary, secondary, tertiary, tertiary-no-background, tertiary-on-primary, error, warning, success. If left empty,
     * the default button style will be applied.
     */
    type: {
      type: String,
      validator(e) {
        return ["primary", "secondary", "tertiary", "tertiary-no-background", "tertiary-on-primary", "error", "warning", "success"].indexOf(e) !== -1;
      },
      default: null
    },
    /**
     * Icon to show for the toggle menu button
     * when more than one action is inside the actions component.
     * Only replace the default three-dot icon if really necessary.
     */
    defaultIcon: {
      type: String,
      default: ""
    },
    /**
     * Aria label for the actions menu.
     *
     * If `menuName` is defined this will not be used to prevent
     * any accessible name conflicts. This ensures that the
     * element can be activated via voice input.
     */
    ariaLabel: {
      type: String,
      default: J("Actions")
    },
    /**
     * @deprecated To be removed in @nextcloud/vue 9. Migration guide: remove ariaHidden prop from NcAction* components.
     * @todo Add a check in @nextcloud/vue 9 that this prop is not provided,
     * otherwise root element will inherit incorrect aria-hidden.
     */
    ariaHidden: {
      type: Boolean,
      default: null
    },
    /**
     * Wanted direction of the menu
     */
    placement: {
      type: String,
      default: "bottom"
    },
    /**
     * DOM element for the actions' popover boundaries
     */
    boundariesElement: {
      type: Element,
      default: () => document.querySelector("body")
    },
    /**
     * Selector for the actions' popover container
     */
    container: {
      type: [String, Object, Element, Boolean],
      default: "body"
    },
    /**
     * Disabled state of the main button (single action or menu toggle)
     */
    disabled: {
      type: Boolean,
      default: !1
    },
    /**
     * Display x items inline out of the dropdown menu
     * Will be ignored if `forceMenu` is set
     */
    inline: {
      type: Number,
      default: 0
    }
  },
  emits: [
    "open",
    "update:open",
    "close",
    "focus",
    "blur"
  ],
  data() {
    return {
      opened: this.open,
      focusIndex: 0,
      randomId: `menu-${j()}`,
      isSemanticMenu: !1,
      isSemanticNavigation: !1,
      isSemanticPopoverLike: !1
    };
  },
  computed: {
    triggerBtnType() {
      return this.type || (this.primary ? "primary" : this.menuName ? "secondary" : "tertiary");
    }
  },
  watch: {
    // Watch parent prop
    open(e) {
      e !== this.opened && (this.opened = e);
    }
  },
  methods: {
    /**
     * Do we have exactly one Action and
     * is it allowed as a standalone element?
     *
     * @param {Array} action The action to check
     * @return {boolean}
     */
    isValidSingleAction(e) {
      var r, p, c, d, f;
      const s = (f = (c = (p = (r = e == null ? void 0 : e.componentOptions) == null ? void 0 : r.Ctor) == null ? void 0 : p.extendOptions) == null ? void 0 : c.name) != null ? f : (d = e == null ? void 0 : e.componentOptions) == null ? void 0 : d.tag;
      return ["NcActionButton", "NcActionLink", "NcActionRouter"].includes(s);
    },
    // MENU STATE MANAGEMENT
    openMenu(e) {
      this.opened || (this.opened = !0, this.$emit("update:open", !0), this.$emit("open"));
    },
    closeMenu(e = !0) {
      this.opened && (this.opened = !1, this.$refs.popover.clearFocusTrap({ returnFocus: e }), this.$emit("update:open", !1), this.$emit("close"), this.focusIndex = 0, this.$refs.menuButton.$el.focus());
    },
    onOpen(e) {
      this.$nextTick(() => {
        this.focusFirstAction(e);
      });
    },
    // MENU KEYS & FOCUS MANAGEMENT
    // focus nearest focusable item on mouse move
    // DO NOT change the focus if the target is already focused
    // this will prevent issues with input being unfocused
    // on mouse move
    onMouseFocusAction(e) {
      if (document.activeElement === e.target)
        return;
      const s = e.target.closest("li");
      if (s && this.$refs.menu.contains(s)) {
        const r = s.querySelector(a);
        if (r) {
          const c = [...this.$refs.menu.querySelectorAll(a)].indexOf(r);
          c > -1 && (this.focusIndex = c, this.focusAction());
        }
      }
    },
    /**
     * Dispatches the keydown listener to different handlers
     *
     * @param {object} event The keydown event
     */
    onKeydown(e) {
      e.key === "Tab" && !this.isSemanticPopoverLike && this.closeMenu(!1), e.key === "ArrowUp" && this.focusPreviousAction(e), e.key === "ArrowDown" && this.focusNextAction(e), e.key === "PageUp" && this.focusFirstAction(e), e.key === "PageDown" && this.focusLastAction(e), e.key === "Escape" && (this.closeMenu(), e.preventDefault());
    },
    removeCurrentActive() {
      const e = this.$refs.menu.querySelector("li.active");
      e && e.classList.remove("active");
    },
    focusAction() {
      const e = this.$refs.menu.querySelectorAll(a)[this.focusIndex];
      if (e) {
        this.removeCurrentActive();
        const s = e.closest("li.action");
        e.focus(), s && s.classList.add("active");
      }
    },
    focusPreviousAction(e) {
      this.opened && (this.focusIndex === 0 ? this.focusLastAction(e) : (this.preventIfEvent(e), this.focusIndex = this.focusIndex - 1), this.focusAction());
    },
    focusNextAction(e) {
      if (this.opened) {
        const s = this.$refs.menu.querySelectorAll(a).length - 1;
        this.focusIndex === s ? this.focusFirstAction(e) : (this.preventIfEvent(e), this.focusIndex = this.focusIndex + 1), this.focusAction();
      }
    },
    focusFirstAction(e) {
      if (this.opened) {
        this.preventIfEvent(e);
        const s = [...this.$refs.menu.querySelectorAll(a)].findIndex((r) => r.parentElement.getAttribute("aria-selected"));
        this.focusIndex = s > -1 ? s : 0, this.focusAction();
      }
    },
    focusLastAction(e) {
      this.opened && (this.preventIfEvent(e), this.focusIndex = this.$refs.menu.querySelectorAll(a).length - 1, this.focusAction());
    },
    preventIfEvent(e) {
      e && (e.preventDefault(), e.stopPropagation());
    },
    onFocus(e) {
      this.$emit("focus", e);
    },
    onBlur(e) {
      this.$emit("blur", e);
    }
  },
  /**
   * The render function to display the component
   *
   * @param {Function} h The function to create VNodes
   * @return {object|undefined} The created VNode
   */
  render(e) {
    const s = (this.$slots.default || []).filter(
      (t) => {
        var n, i, o, l;
        return ((n = t == null ? void 0 : t.componentOptions) == null ? void 0 : n.tag) || ((l = (o = (i = t == null ? void 0 : t.componentOptions) == null ? void 0 : i.Ctor) == null ? void 0 : o.extendOptions) == null ? void 0 : l.name);
      }
    ), r = (t) => {
      var n, i, o, l, m;
      return (m = (o = (i = (n = t == null ? void 0 : t.componentOptions) == null ? void 0 : n.Ctor) == null ? void 0 : i.extendOptions) == null ? void 0 : o.name) != null ? m : (l = t == null ? void 0 : t.componentOptions) == null ? void 0 : l.tag;
    }, p = ["NcActionButton", "NcActionButtonGroup", "NcActionCheckbox", "NcActionRadio"], c = ["NcActionInput", "NcActionTextEditable"], d = ["NcActionLink", "NcActionRouter"], f = s.some((t) => c.includes(r(t))), A = s.some((t) => p.includes(r(t))), H = s.some((t) => d.includes(r(t)));
    this.isSemanticMenu = A && !f, this.isSemanticNavigation = H && !A && !f, this.isSemanticPopoverLike = !this.isSemanticMenu && !this.isSemanticNavigation;
    let u = s.filter(this.isValidSingleAction);
    if (this.forceMenu && u.length > 0 && this.inline > 0 && (Q.util.warn("Specifying forceMenu will ignore any inline actions rendering."), u = []), s.length === 0)
      return;
    const g = (t) => {
      var b, I, N, S, _, $, B, k, O, L, M, h, E, w, P, F, D, T, q, C, R, z, G;
      const n = ((N = (I = (b = t == null ? void 0 : t.data) == null ? void 0 : b.scopedSlots) == null ? void 0 : I.icon()) == null ? void 0 : N[0]) || e("span", { class: ["icon", (_ = (S = t == null ? void 0 : t.componentOptions) == null ? void 0 : S.propsData) == null ? void 0 : _.icon] }), i = (($ = t == null ? void 0 : t.data) == null ? void 0 : $.attrs) || {}, o = (k = (B = t == null ? void 0 : t.componentOptions) == null ? void 0 : B.listeners) == null ? void 0 : k.click, l = (E = (h = (M = (L = (O = t == null ? void 0 : t.componentOptions) == null ? void 0 : O.children) == null ? void 0 : L[0]) == null ? void 0 : M.text) == null ? void 0 : h.trim) == null ? void 0 : E.call(h), m = ((P = (w = t == null ? void 0 : t.componentOptions) == null ? void 0 : w.propsData) == null ? void 0 : P.ariaLabel) || l, x = this.forceName ? l : "";
      let y = (D = (F = t == null ? void 0 : t.componentOptions) == null ? void 0 : F.propsData) == null ? void 0 : D.title;
      return this.forceName || y || (y = l), e(
        "NcButton",
        {
          class: [
            "action-item action-item--single",
            (T = t == null ? void 0 : t.data) == null ? void 0 : T.staticClass,
            (q = t == null ? void 0 : t.data) == null ? void 0 : q.class
          ],
          attrs: {
            ...i,
            "aria-label": m,
            title: y
          },
          ref: (C = t == null ? void 0 : t.data) == null ? void 0 : C.ref,
          props: {
            // If it has a menuName, we use a secondary button
            type: this.type || (x ? "secondary" : "tertiary"),
            disabled: this.disabled || ((z = (R = t == null ? void 0 : t.componentOptions) == null ? void 0 : R.propsData) == null ? void 0 : z.disabled),
            ...(G = t == null ? void 0 : t.componentOptions) == null ? void 0 : G.propsData
          },
          on: {
            focus: this.onFocus,
            blur: this.onBlur,
            // If we have a click listener,
            // we bind it to execute on click and forward the click event
            ...!!o && {
              click: (V) => {
                o && o(V);
              }
            }
          }
        },
        [
          e("template", { slot: "icon" }, [n]),
          x
        ]
      );
    }, v = (t) => {
      var i, o;
      const n = ((i = this.$slots.icon) == null ? void 0 : i[0]) || (this.defaultIcon ? e("span", { class: ["icon", this.defaultIcon] }) : e("DotsHorizontal", {
        props: {
          size: 20
        }
      }));
      return e(
        "NcPopover",
        {
          ref: "popover",
          props: {
            delay: 0,
            handleResize: !0,
            shown: this.opened,
            placement: this.placement,
            boundary: this.boundariesElement,
            container: this.container,
            popoverBaseClass: "action-item__popper",
            // Menu and navigation should not have focus trap
            // Tab should close the menu and move focus to the next UI element
            setReturnFocus: this.isSemanticPopoverLike ? (o = this.$refs.menuButton) == null ? void 0 : o.$el : null,
            focusTrap: this.isSemanticPopoverLike
          },
          // For some reason the popover component
          // does not react to props given under the 'props' key,
          // so we use both 'attrs' and 'props'
          attrs: {
            delay: 0,
            handleResize: !0,
            shown: this.opened,
            placement: this.placement,
            boundary: this.boundariesElement,
            container: this.container,
            ...this.manualOpen && { triggers: [] }
          },
          on: {
            show: this.openMenu,
            "after-show": this.onOpen,
            hide: this.closeMenu
          }
        },
        [
          e("NcButton", {
            class: "action-item__menutoggle",
            props: {
              type: this.triggerBtnType,
              disabled: this.disabled
            },
            slot: "trigger",
            ref: "menuButton",
            attrs: {
              "aria-haspopup": this.isSemanticMenu ? "menu" : null,
              "aria-label": this.menuName ? null : this.ariaLabel,
              "aria-controls": this.opened ? this.randomId : null,
              // Do not add aria-expanded="true" when it is closed
              "aria-expanded": this.opened ? "true" : void 0
            },
            on: {
              focus: this.onFocus,
              blur: this.onBlur
            }
          }, [
            e("template", { slot: "icon" }, [n]),
            this.menuName
          ]),
          e("div", {
            class: {
              open: this.opened
            },
            attrs: {
              tabindex: "-1"
            },
            on: {
              keydown: this.onKeydown,
              mousemove: this.onMouseFocusAction
            },
            ref: "menu"
          }, [
            e("ul", {
              attrs: {
                id: this.randomId,
                tabindex: "-1",
                role: this.isSemanticMenu ? "menu" : void 0
              }
            }, [
              t
            ])
          ])
        ]
      );
    };
    if (s.length === 1 && u.length === 1 && !this.forceMenu)
      return g(u[0]);
    if (this.$nextTick(() => {
      this.opened && this.$refs.menu && (this.$refs.menu.querySelector("li.active") || []).length === 0 && this.focusFirstAction();
    }), u.length > 0 && this.inline > 0) {
      const t = u.slice(0, this.inline), n = s.filter((i) => !t.includes(i));
      return e(
        "div",
        {
          class: [
            "action-items",
            `action-item--${this.triggerBtnType}`
          ]
        },
        [
          // Render inline actions
          ...t.map(g),
          // render the rest within the popover menu
          n.length > 0 ? e(
            "div",
            {
              class: [
                "action-item",
                {
                  "action-item--open": this.opened
                }
              ]
            },
            [
              v(n)
            ]
          ) : null
        ]
      );
    }
    return e(
      "div",
      {
        class: [
          "action-item action-item--default-popover",
          `action-item--${this.triggerBtnType}`,
          {
            "action-item--open": this.opened
          }
        ]
      },
      [
        v(s)
      ]
    );
  }
}, ee = null, te = null;
var se = /* @__PURE__ */ Y(
  Z,
  ee,
  te,
  !1,
  null,
  "fd3aaaa4",
  null,
  null
);
const pe = se.exports;
export {
  pe as default
};
